import React, { useState, useRef, useEffect } from 'react';
import { Phone, CalendarCheck, FileSearch, Hammer, Home, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

const processSteps = [
  {
    icon: Phone,
    step: 1,
    title: "Contact",
    fullTitle: "Contact Us Today",
    description: "Reach out to our team via phone, email, or our online form. We're available 24/7 to answer your questions and schedule your free consultation."
  },
  {
    icon: CalendarCheck,
    step: 2,
    title: "Appointment",
    fullTitle: "Schedule Your Appointment",
    description: "We'll work with your schedule to set up a convenient time for our certified inspector to visit your property and assess your roofing needs."
  },
  {
    icon: FileSearch,
    step: 3,
    title: "Estimate",
    fullTitle: "Receive Your Detailed Estimate",
    description: "Get a comprehensive, transparent estimate with itemized costs, material options, and timeline. No hidden fees, no surprises—just honest pricing."
  },
  {
    icon: Hammer,
    step: 4,
    title: "Installation",
    fullTitle: "Professional Installation",
    description: "Our GAF Master Elite certified crew handles your project with precision and care. We keep you updated throughout the entire installation process."
  },
  {
    icon: Home,
    step: 5,
    title: "Final Touches",
    fullTitle: "Final Inspection & Warranty",
    description: "We conduct a thorough final walkthrough, complete cleanup, and activate your enhanced warranty. Your satisfaction is guaranteed."
  }
];

export const OurProcess = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const goToPrev = () => {
    setActiveStep((prev) => (prev === 0 ? processSteps.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setActiveStep((prev) => (prev === processSteps.length - 1 ? 0 : prev + 1));
  };

  // Track scroll position for mobile carousel
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.offsetWidth * 0.70;
      const spacerWidth = container.offsetWidth * 0.15;
      const adjustedScroll = Math.max(0, scrollLeft - spacerWidth);
      const newIndex = Math.round(adjustedScroll / cardWidth);
      setActiveIndex(Math.min(Math.max(newIndex, 0), processSteps.length - 1));
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to index for mobile
  const scrollToIndex = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const cardWidth = container.offsetWidth * 0.70;
    const spacerWidth = container.offsetWidth * 0.15;
    container.scrollTo({ left: spacerWidth + index * cardWidth, behavior: 'smooth' });
  };

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-blue-50/50 to-background overflow-hidden">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-secondary font-semibold tracking-widest text-sm uppercase mb-2 block">
            Our Proven Process
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-primary">
            A Roofing Process That Works
          </h2>
        </div>

        {/* Desktop: Step Icons Timeline */}
        <div className="hidden md:block relative mb-8">
          {/* Connecting Line */}
          <div className="absolute top-1/2 left-[10%] right-[10%] h-1 bg-blue-100 -translate-y-1/2 rounded-full" />
          
          {/* All 5 steps */}
          <div className="flex justify-between items-center relative z-10 px-4">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              const isActive = index === activeStep;
              
              return (
                <button
                  key={index}
                  onClick={() => setActiveStep(index)}
                  className="flex flex-col items-center gap-3 group"
                >
                  {/* Icon Circle */}
                  <div className="relative">
                    {/* Sparkle decorations for active */}
                    {isActive && (
                      <>
                        <Sparkles className="absolute -top-2 -right-2 w-4 h-4 text-amber-400 animate-pulse" />
                        <Sparkles className="absolute -bottom-1 -left-2 w-3 h-3 text-amber-300 animate-pulse delay-150" />
                      </>
                    )}
                    
                    {/* Outer ring */}
                    <div className={cn(
                      "w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300",
                      isActive 
                        ? "bg-gradient-to-br from-amber-200 via-amber-300 to-amber-400 shadow-lg shadow-amber-200/50" 
                        : "bg-gray-100 group-hover:bg-blue-50"
                    )}>
                      {/* Inner circle with icon */}
                      <div className={cn(
                        "w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300",
                        isActive 
                          ? "bg-blue-600 shadow-inner" 
                          : "bg-white group-hover:bg-blue-50"
                      )}>
                        <IconComponent className={cn(
                          "w-7 h-7 transition-colors duration-300",
                          isActive ? "text-white" : "text-blue-600"
                        )} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Step label */}
                  <div className="text-center">
                    <span className={cn(
                      "text-xs font-medium uppercase tracking-wide block",
                      isActive ? "text-secondary" : "text-muted-foreground"
                    )}>
                      Step {step.step}
                    </span>
                    <span className={cn(
                      "font-semibold transition-colors",
                      isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                    )}>
                      {step.title}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop: Content Panel */}
        <div className="hidden md:block bg-white rounded-2xl shadow-lg p-6 md:p-8 text-center max-w-3xl mx-auto border border-blue-100 mb-8">
          <h3 className="text-2xl md:text-3xl font-bold text-primary mb-4 transition-all duration-300">
            {processSteps[activeStep].fullTitle}
          </h3>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {processSteps[activeStep].description}
          </p>
        </div>

        {/* Desktop navigation arrows */}
        <div className="hidden md:flex justify-center gap-4">
          <button
            onClick={goToPrev}
            className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goToNext}
            className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile: Swipeable Carousel with Connected Timeline */}
        <div className="md:hidden">
          {/* Swipeable Cards */}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* Left spacer to center first card */}
            <div className="flex-shrink-0 w-[15vw]" />
            
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              const isFirst = index === 0;
              const isLast = index === processSteps.length - 1;
              
              return (
                <div
                  key={index}
                  className="flex-shrink-0 w-[70vw] snap-center"
                >
                  {/* Card Content */}
                  <div className="flex flex-col items-center overflow-visible">
                    {/* Timeline with Icon */}
                    <div className="relative w-full flex items-center justify-center mb-6 overflow-visible">
                      {/* Continuous line that extends beyond card boundaries */}
                      <div 
                        className="absolute h-1 bg-blue-100"
                        style={{
                          left: isFirst ? '50%' : '-50%',
                          right: isLast ? '50%' : '-50%',
                        }}
                      />
                      
                      {/* Icon Circle - Larger size */}
                      <div className="relative z-10">
                        <Sparkles className="absolute -top-4 -right-4 w-6 h-6 text-amber-400 animate-pulse" />
                        <Sparkles className="absolute -bottom-2 -left-4 w-5 h-5 text-amber-300 animate-pulse delay-150" />
                        
                        {/* Outer ring with golden gradient - w-24 h-24 */}
                        <div className="w-24 h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-200 via-amber-300 to-amber-400 shadow-xl shadow-amber-200/50">
                          {/* Inner circle with icon - w-20 h-20 */}
                          <div className="w-20 h-20 rounded-full flex items-center justify-center bg-blue-600 shadow-inner">
                            <IconComponent className="w-10 h-10 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Step Label */}
                    <div className="text-center mb-4">
                      <span className="text-xs font-medium uppercase tracking-wide text-secondary block">
                        Step {step.step}
                      </span>
                      <span className="text-lg font-bold text-primary">
                        {step.title}
                      </span>
                    </div>
                    
                    {/* Description */}
                    <div className="text-center px-2">
                      <h3 className="text-xl font-bold text-primary mb-3">
                        {step.fullTitle}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Right spacer to center last card */}
            <div className="flex-shrink-0 w-[15vw]" />
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-4">
            {processSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={cn(
                  "h-2 rounded-full transition-all duration-300",
                  index === activeIndex 
                    ? "w-6 bg-amber-500" 
                    : "w-2 bg-gray-300 hover:bg-gray-400"
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};